import angular from 'angular';
import $ from 'jquery';
import _ from 'lodash-es';
import APIV5 from '~/models/api';
import {
	toCanvas,
} from 'html-to-image';
import { ConfirmDeleteModal } from '~/utils/modals';
import { Confirm } from '~/scripts/services/modal/modal';
import { NodeService as NodeServiceModel } from '~/scripts/services/model/node-service';
import Utils from '~/scripts/services/utils';

/**
 * Add the given element to the posse with the given id, creating the group if it at first does not exist.
 * @callback jsPlumbExtraAddToPoseFunction
 * @param {string|Element} el Element to add.
 * @param {string|Object} spec Variable args parameters. Each argument can be a either a String, indicating
 * the ID of a Posse to which the element should be added as an active participant, or an Object containing
 * `{ id:"posseId", active:false/true}`. In the latter case, if `active` is not provided it is assumed to be
 * true.
 * @returns {import('jsplumb').Posse|import('jsplumb').Posse[]} The Posse(s) to which the element(s) was/were added.
 */

/**
 * Remove the given element from all Posses to which it belongs.
 * @callback jsPlumbExtraRemoveFromAllPosses
 * @param {string|string[]|Element|Element[]} el Element to remove from Posses.
 */

/**
 * @typedef {Object} jsPlumbExtraDragOptions
 * @augments import('jsplumb').DragOptions
 * @property {boolean} disabled
 */

/**
 * Either makes the given element draggable, or identifies it as an element inside which some identified list
 * of elements may be draggable.
 * @callback jsPlumbExtraDraggableFunction
 * @param {any} el
 * @param {jsPlumbExtraDragOptions} params
 * @returns {any[]}
 */

/**
 * @typedef {Object} jsPlumbExtra
 * @property {jsPlumbExtraAddToPoseFunction} addToPosse
 * @property {jsPlumbExtraDraggableFunction} draggable
 * @property {jsPlumbExtraRemoveFromAllPosses} removeFromAllPosses
 * @property {unknown} actions
 * @property {() => Promise<unknown>} loadActions
 */

export default class Designer {
	/* @ngInject */
	/**
	 * @param {import('angular').auto.IInjectorService} $injector
	 */
	constructor($injector) {
		this.$injector = $injector;

		/** @type {any} */
		this.$rootScope = $injector.get('$rootScope');

		/** @type {import('angular').IQService} */
		this.$q = $injector.get('$q');

		/** @type {import('angular').ISCEService} */
		this.$sce = $injector.get('$sce');

		/** @type {import('angular').ITimeoutService} */
		this.$timeout = $injector.get('$timeout');

		/** @type {import('angular').translate.ITranslateService} */
		this.$translate = $injector.get('$translate');

		/** @type {import('angular').ui.bootstrap.IModalService} */
		this.$uibModal = $injector.get('$uibModal');

		/** @type {import('angular-jsplumb').jsPlumbService} */
		this.jsPlumbService = $injector.get('jsPlumbService');

		/** @type {NodeServiceModel} */
		this.NodeService = $injector.get('NodeService');

		/** @type {any} */
		this.Notification = $injector.get('Notification');

		this.User = $injector.get('User');

		/** @type {boolean} */
		this.connecting = false;

		/** @type {boolean} */
		this.create = false;

		/** @type {boolean} */
		this.edit = false;

		/** @type {?string} */
		this.id = null;

		/** @type {?import('jsplumb').jsPlumbInstance & jsPlumbExtra} */
		this.jsp = null;

		/** @type {any} */
		this.jspOptions = {
			PaintStyle:          {
				lineWidth:   2,
				strokeStyle: '#354291',
			},
			MaxConnections:      -1,
			Endpoint:            [
				'Dot',
				{
					cssClass: 'endpoint',
					radius:   8,
				},
			],
			EndpointStyle:       {
				fill:   '#bbb',
				stroke: '#bbb',
			},
			ConnectorHoverStyle: {
				strokeWidth:   3,
				stroke:        '#3a8bd1',
				outlineWidth:  5,
				outlineStroke: 'white',
			},
			ConnectionOverlays:  [
				[
					'PlainArrow',
					{
						id:       'arrow',
						length:   9,
						location: 1,
					},
				],
			],
		};

		/** @type {boolean} */
		this.jspReady = false;

		/** @type {boolean} */
		this.loading = false;

		/** @type {boolean} */
		this.modified = true;

		/** @type {boolean} */
		this.new = true;

		/** @type {number} */
		this.scale = 1;

		/** @type {any} */
		this.schema = null;

		/** @type {JQueryCoordinates | undefined} */
		this.schemaOffset = undefined;

		/** @type {boolean} */
		this.zoomInDisabled = false;

		/** @type {boolean} */
		this.zoomOutDisabled = false;

		// noinspection JSUnresolvedFunction
		this.jsPlumbService.jsplumbInit().then(() => {
			this.jspReady = true;
			return true;
		}).catch(() => {
			this.jspReady = false;
		});
	}

	/**
	 * Closes the tab
	 *
	 * @returns {Promise<any>}
	 */
	async close() {
		const deferred = this.$q.defer();

		if (this.modified) {
			const modal = new Confirm(this.$injector);
			const answer = await modal.open({
				accept: {
					label: 'Annuler les changements',
					type: 'danger',
				},
				cancel: {
					label: this.$translate.instant('common.close'),
				},
				title: 'Confirmation de fermeture',
				body: 'Ce schéma n\'a pas été sauvegardé !',
			});

			if (answer) {
				this.$timeout(() => {
					deferred.resolve();
				});
			}
		}
		else {
			this.$timeout(() => {
				deferred.resolve();
			});
		}

		return deferred.promise;
	}

	/**
	 * Delete a schema
	 *
	 * @returns {Promise<any>}
	 */
	async delete() {
		if (!this.schema?.name) {
			return;
		}
		const modal = new ConfirmDeleteModal(this.$injector);
		const answer = await modal.open(this.schema.name);

		if (answer) {
			/**
			 * @param {any} error
			 */
			this.NodeService.delete(this.schema.kid)
				.then(() => this.close())
				.catch(() => {});
		}
	}

	/**
	 * Export the schema according to the format
	 *
	 * @param {'xlsx' | 'json' | 'png'} format Format of the output export
	 * @param {string} version JSON version
	 * @returns {Promise<any>} Returns true if success
	 */
	async export(format, version = '1.2') {
		// eslint-disable-next-line unicorn/no-this-assignment
		const self = this;

		if (!this.schema) {
			console.warn('no schema!');
			return false;
		}

		if (format === 'xlsx') {
			const result = await APIV5.instance.classDesignerExport(
				{
					kid: this.schema.kid,
					format: 'excel',
				},
				{
					id: 'designer.export',
				},
			);

			const disposition = result.headers('Content-Disposition');
			if (disposition && disposition.includes('attachment')) {
				// eslint-disable-next-line no-useless-backreference
				const matches = /filename[^\n;]*=(UTF-\d["']*)?((["']).*?\.$\2|[^\n;]*)?/.exec(disposition);
				if (matches !== null && matches[2]) {
					const filename = decodeURIComponent(matches[2].replaceAll(/('|\\"|")/g, ''));
					Utils.download(result.buffer, result.headers('Content-Type'), filename);
				}
			}
		}
		if (format === 'json') {
			const json = this.schema.export(version);
			// eslint-disable-next-line no-console
			console.log(json);
			const filename = `${this.schema.name}.json`;
			const blob = new Blob([angular.toJson(json)], { type: 'application/json' });

			// @ts-ignore
			if (window.navigator.msSaveBlob === undefined) {
				const URL = window.URL || window.webkitURL;
				const downloadUrl = URL.createObjectURL(blob);

				const a = document.createElement('a');
				if (a.download === undefined) {
					window.location.assign(downloadUrl);
				}
				else {
					a.href = downloadUrl;
					a.download = filename;
					document.body.append(a);
					a.click();
				}

				self.$timeout(() => {
					URL.revokeObjectURL(downloadUrl);
				}, 100); // cleanup
			}
			else {
				// @ts-ignore
				window.navigator.msSaveBlob(blob, filename);
			}
			return true;
		}
		if (format === 'png') {
			const element = $('.tab-pane.active').find('.schema-editor').get(0);
			const oldTabEdit = this.edit;

			this.edit = false;

			let minWidth = 9000;
			let minHeight = 9000;
			let maxWidth = 1;
			let maxHeight = 1;

			// @ts-ignore
			$(element).find('> .node').each((k, s) => {
				minWidth = Math.min(minWidth, $(s).position().left - 10);
				minHeight = Math.min(minHeight, $(s).position().top - 10);
				// @ts-ignore
				maxWidth = Math.max(maxWidth, $(s).position().left + $(s).outerWidth() + 10);
				// @ts-ignore
				maxHeight = Math.max(maxHeight, $(s).position().top + $(s).outerHeight() + 10);
			});

			// @ts-ignore
			toCanvas(element, {
				width: maxWidth,
				height: maxHeight,
			}).then(canvas => {
				const $dlLink = $('<a/>').attr({
					download: `${this.schema.name}.png`,
					href:     canvas.toDataURL('image/png'),
					dataset:  {
						downloadurl: [
							'image/png',
							`${self.schema.name}.png`,
							canvas.toDataURL('image/png'),
						].join(':'),
					},
				}).hide();

				// @ts-ignore
				$(element).append($dlLink);
				if ($dlLink) {
					$dlLink[0].click();
				}
				$dlLink.remove();

				this.edit = oldTabEdit;
				return true;
			}).catch(error => console.error(error));
			return true;
		}

		return false;
	}

	/**
	 * Saves the schema
	 *
	 * @returns {Promise<any>} Promise result
	 */
	async save() {
		if (this.schema) {
			this.schema.kjson = this.schema.export();

			const schema = _.cloneDeep(this.schema);
			schema.nodes.length = 0;
			schema.kid = `K_NODE;${this.id}`;
			/**
			 * @param {any} result
			 */
			if (await this.NodeService.update(schema)) {
				this.create = false;
				this.modified = false;
				return true;
			}
		}
		return false;
	}

	/**
	 * Set the display scale
	 *
	 * @param {number} scale Scale of the display (1.0 is the 1:1 scale)
	 */
	setScale(scale) {
		const $schemaEditor = $('.tab-pane.active').find('.schema-editor');

		this.scale = scale;
		this.jsp?.setZoom(scale);

		// @ts-ignore
		$schemaEditor.css('transform', `scale(${this.scale})`).offset(this.schemaOffset);
	}

	/**
	 * Increases the display zoom
	 */
	zoomIn() {
		const scale = Math.round((this.scale + 0.1) * 10) / 10;

		if (scale >= 3) {
			this.zoomInDisabled = true;
		}
		else {
			this.zoomInDisabled = false;
			this.setScale(scale);
		}
	}

	/**
	 * Decreases the display zoom
	 */
	zoomOut() {
		const scale = Math.round((this.scale - 0.1) * 10) / 10;

		if (scale <= 0.3) {
			this.zoomOutDisabled = true;
		}
		else {
			this.zoomOutDisabled = false;
			this.setScale(scale);
		}
	}
}
